/**
 * DocumentUploadModal Component (AC-810, AC-818)
 *
 * Modal workflow for uploading documents to the knowledge base.
 * Manages file selection, upload progress, and completion summary.
 * Includes pre-upload duplicate detection and replacement warnings (AC-818).
 *
 * Technical Design Reference: docs/technical/TDD-AC-804-knowledge-base-file-upload.md
 * Dedup Design Reference: docs/technical/TDD-AC-815-knowledge-base-deduplication.md
 */

import React, { useState, useEffect, useCallback } from 'react';
import { AlertCircle, CheckCircle, Upload, FileText, X } from 'lucide-react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import FileUploadZone from './FileUploadZone';
import UploadProgressItem from './UploadProgressItem';
import apiService from '../../services/api';
import {
  KnowledgeBase,
  UploadStatus,
  DocumentUploadResponse,
  DocumentProcessingResult,
  DuplicateInfo,
} from '../../types';

interface FileUploadState {
  file: File;
  status: UploadStatus;
  progress: number;
  error?: string;
  chunksCreated?: number;
  replaced?: boolean;
  chunksRemoved?: number;
}

interface DocumentUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId?: number;
  onUploadComplete: (entries: KnowledgeBase[]) => void;
}

type ModalPhase = 'selection' | 'uploading' | 'completed' | 'error';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const DocumentUploadModal: React.FC<DocumentUploadModalProps> = ({
  isOpen,
  onClose,
  projectId,
  onUploadComplete,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<FileUploadState[]>([]);
  const [phase, setPhase] = useState<ModalPhase>('selection');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResults, setUploadResults] = useState<DocumentUploadResponse | null>(null);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState<Record<string, DuplicateInfo> | null>(null);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedFiles([]);
      setPhase('selection');
      setUploadProgress(0);
      setUploadResults(null);
      setGlobalError(null);
      setShowCancelConfirm(false);
      setDuplicateInfo(null);
    }
  }, [isOpen]);

  const handleFilesSelected = useCallback((files: File[]) => {
    const newFiles: FileUploadState[] = files.map(file => ({
      file,
      status: 'pending' as UploadStatus,
      progress: 0,
    }));
    setSelectedFiles(newFiles);
    setGlobalError(null);
    setDuplicateInfo(null);

    // Check for duplicate documents
    const filenames = files.map(f => f.name);
    apiService.checkDuplicateDocuments(filenames, projectId)
      .then(response => {
        setDuplicateInfo(response.duplicates);
      })
      .catch(() => {
        // Proceed silently on failure - don't block upload
      });
  }, [projectId]);

  const handleRemoveFile = useCallback((index: number) => {
    setSelectedFiles(prev => {
      const removed = prev[index];
      const updated = prev.filter((_, i) => i !== index);
      // Clear stale duplicate info for the removed file
      if (removed && duplicateInfo && duplicateInfo[removed.file.name]) {
        setDuplicateInfo(prevInfo => {
          if (!prevInfo) return prevInfo;
          const copy = { ...prevInfo };
          delete copy[removed.file.name];
          return Object.keys(copy).length > 0 ? copy : null;
        });
      }
      return updated;
    });
  }, [duplicateInfo]);

  const handleUpload = useCallback(async () => {
    if (selectedFiles.length === 0) return;

    setPhase('uploading');
    setGlobalError(null);

    // Mark all files as uploading
    setSelectedFiles(prev =>
      prev.map(f => ({ ...f, status: 'uploading' as UploadStatus, progress: 0 }))
    );

    try {
      // Get just the File objects
      const files = selectedFiles.map(f => f.file);

      // Call the API service
      const response = await apiService.uploadDocuments(
        files,
        projectId,
        (progress: number) => {
          setUploadProgress(progress);
          // Update all files with the current progress
          setSelectedFiles(prev =>
            prev.map(f => ({
              ...f,
              status: progress < 100 ? 'uploading' : 'processing',
              progress,
            }))
          );
        }
      );

      setUploadResults(response);

      // Update file states based on results
      if (response && response.results) {
        setSelectedFiles(prev =>
          prev.map(fileState => {
            const result = response.results.find(
              r => r.filename === fileState.file.name
            );
            if (result) {
              return {
                ...fileState,
                status: result.success ? 'completed' : 'failed',
                progress: 100,
                error: result.error,
                chunksCreated: result.chunks_created,
                replaced: result.replaced,
                chunksRemoved: result.chunks_removed,
              };
            }
            return {
              ...fileState,
              status: 'failed',
              progress: 100,
              error: 'No result received from server',
            };
          })
        );
      }

      setPhase('completed');
    } catch (error: any) {
      console.error('Upload error:', error);
      setGlobalError(error.message || 'Upload failed. Please try again.');
      setPhase('error');

      // Mark all files as failed
      setSelectedFiles(prev =>
        prev.map(f => ({
          ...f,
          status: 'failed' as UploadStatus,
          progress: 0,
          error: 'Upload failed',
        }))
      );
    }
  }, [selectedFiles, projectId]);

  const handleDone = useCallback(() => {
    if (uploadResults) {
      // Create minimal KnowledgeBase objects from the results
      const entries: KnowledgeBase[] = uploadResults.results
        .filter(r => r.success)
        .flatMap(r =>
          r.knowledge_base_ids.map(id => ({
            knowledge_base_id: id,
            // Other fields will be populated when the list refreshes
          } as unknown as KnowledgeBase))
        );
      onUploadComplete(entries);
    }
    onClose();
  }, [uploadResults, onUploadComplete, onClose]);

  const handleRetry = useCallback(() => {
    setPhase('selection');
    setGlobalError(null);
    setUploadResults(null);
    setDuplicateInfo(null);
    setSelectedFiles(prev =>
      prev.map(f => ({
        ...f,
        status: 'pending' as UploadStatus,
        progress: 0,
        error: undefined,
        replaced: undefined,
        chunksRemoved: undefined,
      }))
    );
  }, []);

  const handleCancel = useCallback(() => {
    if (phase === 'uploading') {
      setShowCancelConfirm(true);
    } else {
      onClose();
    }
  }, [phase, onClose]);

  const handleConfirmCancel = useCallback(() => {
    setShowCancelConfirm(false);
    onClose();
  }, [onClose]);

  const selectedFilesCount = selectedFiles.length;
  const isUploading = phase === 'uploading';
  const isCompleted = phase === 'completed';
  const hasError = phase === 'error';

  // Compute summary for completed state
  const successCount = uploadResults?.total_processed || 0;
  const failedCount = uploadResults?.total_failed || 0;

  // Compute replacement count from upload results
  const replacedCount = uploadResults?.results?.filter(r => r.replaced).length || 0;

  // Get list of duplicate files from duplicate info
  const duplicateFiles = duplicateInfo
    ? Object.entries(duplicateInfo).filter(([, info]) => info.exists)
    : [];

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleCancel}>
      <div className="p-6" data-testid="document-upload-modal">
        {/* Header */}
        <h2 className="text-xl font-semibold text-gray-900 dark:text-warm-white mb-4">
          Upload Documents
        </h2>

        {/* Cancel confirmation dialog */}
        {showCancelConfirm && (
          <div className="absolute inset-0 bg-white dark:bg-cap-dark bg-opacity-95 flex items-center justify-center z-10 rounded-lg">
            <div className="p-6 text-center">
              <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Are you sure you want to cancel the upload? Progress will be lost.
              </p>
              <div className="flex justify-center space-x-3">
                <Button
                  onClick={() => setShowCancelConfirm(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 rounded-lg"
                >
                  Continue Upload
                </Button>
                <Button
                  onClick={handleConfirmCancel}
                  className="px-4 py-2 bg-red-500 text-white hover:bg-red-600 rounded-lg"
                >
                  Cancel Upload
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* File Upload Zone - visible in selection phase */}
        {phase === 'selection' && (
          <FileUploadZone
            onFilesSelected={handleFilesSelected}
            disabled={isUploading}
            maxFiles={10}
            maxSizeMB={10}
          />
        )}

        {/* Selected files list */}
        {phase === 'selection' && selectedFilesCount > 0 && (
          <div className="mt-4">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {selectedFilesCount} {selectedFilesCount === 1 ? 'file' : 'files'} selected
            </p>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {selectedFiles.map((fileState, index) => (
                <div
                  key={`${fileState.file.name}-${index}`}
                  className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"
                >
                  <div className="flex items-center min-w-0 mr-2">
                    <FileText className="w-4 h-4 text-gray-400 flex-shrink-0 mr-2" />
                    <span className="text-sm text-gray-900 dark:text-warm-white truncate">{fileState.file.name}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0 ml-2">
                      {formatFileSize(fileState.file.size)}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(index)}
                    className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 rounded"
                    aria-label={`Remove ${fileState.file.name}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            {/* Duplicate file warnings (AC-818) */}
            {duplicateFiles.length > 0 && (
              <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg" data-testid="duplicate-warning">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-yellow-700 dark:text-yellow-300">
                    {duplicateFiles.map(([filename, info]) => (
                      <p key={filename}>
                        <span className="font-medium">{filename}</span> already exists and will be replaced ({info.chunk_count} chunks will be removed)
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Progress items - visible during upload and after */}
        {(isUploading || isCompleted || hasError) && selectedFiles.length > 0 && (
          <div className="mt-4 space-y-3 max-h-64 overflow-y-auto">
            {selectedFiles.map((fileState, index) => (
              <UploadProgressItem
                key={`${fileState.file.name}-${index}`}
                filename={fileState.file.name}
                status={fileState.status}
                progress={fileState.progress}
                error={fileState.error}
                chunksCreated={fileState.chunksCreated}
                replaced={fileState.replaced}
                chunksRemoved={fileState.chunksRemoved}
              />
            ))}
          </div>
        )}

        {/* Completion summary */}
        {isCompleted && uploadResults && (
          <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <p className="text-gray-700 dark:text-gray-300 font-medium">
                {failedCount > 0
                  ? `${successCount} document${successCount !== 1 ? 's' : ''} uploaded, ${failedCount} failed`
                  : replacedCount > 0
                    ? `${successCount} document${successCount !== 1 ? 's' : ''} uploaded (${replacedCount} replaced)`
                    : `${successCount} document${successCount !== 1 ? 's' : ''} uploaded successfully`}
              </p>
            </div>
          </div>
        )}

        {/* Global error message */}
        {hasError && globalError && (
          <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <p className="text-red-700 dark:text-red-400">Upload failed: {globalError}</p>
            </div>
          </div>
        )}

        {/* Footer buttons */}
        <div className="flex justify-end space-x-3 mt-6">
          <Button
            onClick={handleCancel}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 rounded-lg"
          >
            Cancel
          </Button>

          {hasError && (
            <Button
              onClick={handleRetry}
              className="px-4 py-2 border border-primary-500 text-primary-600 bg-white dark:bg-cap-dark hover:bg-primary-50 rounded-lg"
            >
              Retry
            </Button>
          )}

          {phase === 'selection' && (
            <Button
              onClick={handleUpload}
              disabled={selectedFilesCount === 0}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white hover:bg-primary-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload className="w-4 h-4" />
              <span>Upload</span>
            </Button>
          )}

          {isUploading && (
            <Button
              disabled
              className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg opacity-50 cursor-not-allowed"
            >
              <Upload className="w-4 h-4 animate-pulse" />
              <span>Uploading...</span>
            </Button>
          )}

          {isCompleted && (
            <Button
              onClick={handleDone}
              className="px-4 py-2 bg-blue-500 text-white hover:bg-primary-600 rounded-lg"
            >
              Done
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default DocumentUploadModal;
